import React, { useState } from 'react';
import { postData } from '../api/restapi';
import useDeviceActiveSessions from '../../hooks/useDeviceActiveSessions';
import ActiveDebugSessions from './ActiveDebugSessions';
import '../assets/RdQuickStart.css';

const COLLECTORS = [
  {
    collector: 'ODL',
    icon: '📋',
    label: 'Start ODL Collection',
    desc: 'Trigger-based ODL log collection using the default log mask (50 MB max).',
    command: 'CONFIGURE_ENABLE',
  },
  {
    collector: 'MINIDUMP',
    icon: '💥',
    label: 'Start Minidump',
    desc: 'Capture a minidump on crash — uses the existing generated Minidump configuration.',
    command: 'ENABLE',
  },
  {
    collector: 'MICRODUMP',
    icon: '🗜️',
    label: 'Start Microdump',
    desc: 'Lightweight microdump crash capture — uses the existing generated Microdump configuration.',
    command: 'ENABLE',
  },
];

const IDLE = { phase: 'idle', message: '' };

/**
 * RdQuickStart — shared RD quick-launch component.
 *
 * Props:
 *   systemId          {string}    — device system ID; if empty, cards are disabled
 *   userInfo          {string}    — logged-in user's email
 *   role              {string}    — user role (used for active-session detection)
 *   variant           {'cards'|'full'}  — caller hint; no behavioural difference
 *   onSessionStarted  {function}  — optional callback(sessionId) fired on successful POST
 *   onViewSessions    {function}  — optional callback to navigate to RD View tab
 */
function RdQuickStart({ systemId, userInfo, role, variant = 'cards', onSessionStarted, onViewSessions }) {
  // Which collector's confirm modal is open (null = closed)
  const [confirmingCollector, setConfirmingCollector] = useState(null);

  // Per-collector loading/error state (success is surfaced via Active Debug Sessions)
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(COLLECTORS.map((c) => [c.collector, IDLE]))
  );

  // Per-collector error message (shown outside the card)
  const [errorMsg, setErrorMsg] = useState('');

  const { rdSessions, hasActiveRd, refresh } = useDeviceActiveSessions(systemId, userInfo, role);

  const setStatus = (collector, patch) =>
    setStatuses((prev) => ({
      ...prev,
      [collector]: { ...prev[collector], ...patch },
    }));

  const hasSystemId = Boolean(systemId && systemId.trim());

  const handleCardClick = (collector) => {
    if (!hasSystemId || hasActiveRd) return;
    const s = statuses[collector];
    if (s.phase === 'loading') return;
    if (s.phase === 'idle') {
      setConfirmingCollector(collector);
    }
  };

  const handleCancel = () => setConfirmingCollector(null);

  const handleConfirm = async () => {
    const collector = confirmingCollector;
    setConfirmingCollector(null);
    setStatus(collector, { phase: 'loading', message: '' });
    setErrorMsg('');

    const result = await postData('/api/v1/quick-start-remote-diagnostics', {
      systemId: systemId.trim(),
      user: userInfo,
      collector,
    });

    if (result && !result.errorMessage) {
      const sessionId = result.sessionId || result.data?.sessionId || '';
      setStatus(collector, IDLE);
      if (onSessionStarted) onSessionStarted(sessionId);
      refresh();
    } else {
      const msg = result?.errorMessage || 'Unexpected error — please try again.';
      setStatus(collector, IDLE);
      setErrorMsg(msg);
    }
  };

  const confirmingDef = confirmingCollector
    ? COLLECTORS.find((c) => c.collector === confirmingCollector)
    : null;

  return (
    <div className="rdqs-root">
      {/* ── Cards ── */}
      <div className="rdqs-grid">
        {COLLECTORS.map(({ collector, icon, label, desc }) => {
          const s = statuses[collector];
          const isLoading = s.phase === 'loading';
          const isDisabled = !hasSystemId || isLoading || hasActiveRd;

          return (
            <div
              key={collector}
              className={`rdqs-card${isLoading ? ' rdqs-card--loading' : ''}${isDisabled ? ' rdqs-card--disabled' : ''}`}
              onClick={() => !isDisabled && handleCardClick(collector)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyDown={(e) => e.key === 'Enter' && !isDisabled && handleCardClick(collector)}
            >
              <div className="rdqs-icon">{icon}</div>
              <span className="rdqs-kind">Remote Diagnostics</span>
              <div className="rdqs-label">{label}</div>
              <div className="rdqs-desc">{desc}</div>

              {isLoading && (
                <div className="rdqs-status rdqs-status--loading">⏳ Starting…</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Subtle hint when no device is selected */}
      {!hasSystemId && (
        <p className="rdqs-no-device-hint">No device selected — use the table above to pick one.</p>
      )}

      {/* Blocked notice */}
      {hasSystemId && hasActiveRd && (
        <p className="rdqs-no-device-hint" style={{ color: '#92400e' }}>
          ⚠️ An RD session is already running on this device. Only one RD session can run per device — manage it in Active Debug Sessions below.
        </p>
      )}

      {/* Error notice (outside card) */}
      {errorMsg && (
        <p className="rdqs-no-device-hint" style={{ color: '#dc2626' }}>
          ⚠️ {errorMsg}
        </p>
      )}

      {/* Active sessions list */}
      {hasSystemId && (
        <ActiveDebugSessions
          rdSessions={rdSessions}
          rtSessions={[]}
          onViewRd={onViewSessions}
          refresh={refresh}
        />
      )}

      {/* ── Confirm modal ── */}
      {confirmingDef && (
        <div className="rdqs-modal-backdrop" onClick={handleCancel}>
          <div className="rdqs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rdqs-modal-header">
              <h3 className="rdqs-modal-title">
                {confirmingDef.icon} {confirmingDef.label}
              </h3>
              <button className="rdqs-modal-close" onClick={handleCancel} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="rdqs-modal-body">
              {/* Read-only system ID */}
              <div className="rdqs-modal-field">
                <span className="rdqs-modal-field-label">Device System ID</span>
                <span className="rdqs-modal-sysid-pill">{systemId}</span>
              </div>

              {/* Preset values table */}
              <div className="rdqs-preset-grid">
                <div className="rdqs-preset-row">
                  <span className="rdqs-preset-key">Duration</span>
                  <span className="rdqs-preset-val">30 minutes</span>
                </div>
                <div className="rdqs-preset-row">
                  <span className="rdqs-preset-key">Mode</span>
                  <span className="rdqs-preset-val">Immediate start</span>
                </div>
                <div className="rdqs-preset-row">
                  <span className="rdqs-preset-key">Upload frequency</span>
                  <span className="rdqs-preset-val">Every 300 seconds</span>
                </div>
              </div>
            </div>
            <div className="rdqs-modal-footer">
              <button className="rdqs-btn-go" onClick={handleConfirm}>
                ▶ Start Session
              </button>
              <button className="rdqs-btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RdQuickStart;
